"use client"

import { useEffect, useRef, useState } from "react"
import { ExternalLink, X } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { getModalAdTier } from "@/lib/ad-tier"
import { cn } from "@/lib/utils"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { useTranslations } from "next-intl"

/**
 * MediaGalleryAdSlot
 *
 * Renders a native-looking ad card inside an album/gallery navigator.
 *
 * Rules (all configurable via admin):
 *  - Albums shorter than `min_album_size` get zero ads (default 3)
 *  - Any album at or above that floor gets AT LEAST one ad, even when it's
 *    shorter than `images_per_ad` — short albums were previously invisible to
 *    the whole placement (see getAlbumAdIndices)
 *  - Beyond that, 1 ad per every `images_per_ad` real images (default 5)
 *  - First ad always at position `first_ad_position` (default 3, i.e. 4th item)
 *  - Session cooldown: if user saw a gallery ad within `cooldown_minutes`, skip entirely
 *  - Premium subscribers always get zero ads
 */

export interface GalleryAdConfig {
  enabled: boolean
  images_per_ad: number       // 1 ad per this many images (default 5)
  first_ad_position: number   // index of first ad slot (default 3)
  min_album_size: number      // albums shorter than this get no ads (default 3)
  cooldown_minutes: number    // minutes between gallery ad sessions (default 15)
}

export const DEFAULT_GALLERY_AD_CONFIG: GalleryAdConfig = {
  enabled: true,
  images_per_ad: 5,
  first_ad_position: 3,
  min_album_size: 3,
  cooldown_minutes: 15,
}

const COOLDOWN_KEY = "gallery_ad_last_seen"

/** Returns true if the cooldown has passed since the last gallery ad */
function isCooldownExpired(cooldownMinutes: number): boolean {
  if (isAdTestModeOn()) return true
  try {
    const last = localStorage.getItem(COOLDOWN_KEY)
    if (!last) return true
    const elapsed = (Date.now() - parseInt(last, 10)) / 1000 / 60
    return elapsed >= cooldownMinutes
  } catch {
    return true
  }
}

function markCooldown() {
  try {
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()))
  } catch {}
}

/**
 * Given a real album page count and config, returns the set of VIRTUAL indices
 * that should be ad slots.
 *
 * Logic:
 *  - If album < min_album_size → no ads
 *  - Otherwise at least ONE ad, then 1 per images_per_ad real images
 *  - First ad at first_ad_position, then spaced images_per_ad apart
 *
 * The `Math.max(1, …)` matters: with a plain floor(), any album shorter than
 * `images_per_ad` computed to zero ads and silently fell out of the placement
 * entirely — so with the old 10/10 defaults an album had to be 10+ pages to
 * ever show one, and most albums are shorter than that. Now the floor is
 * `min_album_size` alone: clear it and you get an ad, and only longer albums
 * start earning additional ones.
 */
export function getAlbumAdIndices(
  totalPages: number,
  config: GalleryAdConfig
): Set<number> {
  const indices = new Set<number>()
  if (!config.enabled) return indices
  if (totalPages < config.min_album_size) return indices

  const spacing = Math.max(1, config.images_per_ad)
  const maxAds = Math.max(1, Math.floor(totalPages / spacing))

  for (let i = 0; i < maxAds; i++) {
    const position = config.first_ad_position + i * (spacing + 1)
    // Never place a slot past the end of the virtual sequence — with a short
    // album and a large first_ad_position that would produce an index the
    // navigator can never reach, i.e. a silently missing ad.
    if (position > totalPages + i) break
    indices.add(position)
  }
  return indices
}

/**
 * Given a virtual index, returns whether it's an ad slot and the real media index.
 */
export function resolveAlbumIndex(
  virtualIndex: number,
  adIndices: Set<number>
): { isAd: boolean; realIndex: number } {
  if (adIndices.has(virtualIndex)) return { isAd: true, realIndex: -1 }
  let adsBefore = 0
  for (const adIdx of adIndices) {
    if (adIdx < virtualIndex) adsBefore++
  }
  return { isAd: false, realIndex: virtualIndex - adsBefore }
}

// ─── Ad data ─────────────────────────────────────────────────────────────────

interface AdData {
  zone_id: string
  zone_type: "js_embed" | "html_embed" | "banner_image" | "vast" | null
  embed_code: string | null
  banner_image_url: string | null
  banner_click_url: string | null
  network: string | null
  // True native size of the zone's creative (ad_zones.creative_width/height).
  // Needed because an iframe does not grow or center its content: sized
  // w-full/h-full inside the gallery viewer, a 300x250 creative renders
  // stranded in the top-left corner of a ~1100px-tall black void. With the
  // real size known we can render at native size and scale/center it instead.
  creative_width: number | null
  creative_height: number | null
}

/** Don't upscale a fixed-size raster creative past this — beyond roughly 2x
 *  a 300x250 banner starts visibly softening. Fills the gallery viewer far
 *  better than native size while staying legible. */
const MAX_GALLERY_UPSCALE = 2
/** Leave a margin so the ad doesn't butt against the viewer's chrome. */
const GALLERY_FILL_RATIO = 0.9

// ─── Component ────────────────────────────────────────────────────────────────

interface MediaGalleryAdSlotProps {
  slotIndex: number
  variant?: "full" | "thumb"
  cooldownMinutes?: number
  className?: string
  onDismiss?: () => void
}

export function MediaGalleryAdSlot({
  slotIndex,
  variant = "full",
  cooldownMinutes = DEFAULT_GALLERY_AD_CONFIG.cooldown_minutes,
  className,
  onDismiss,
}: MediaGalleryAdSlotProps) {
  const t = useTranslations("mediaGalleryAdSlot")
  const hasConsent = useHasConsent()
  const { isPremiumUser } = useAdStore()
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this slot to show for its only realistic tester.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const adTier = getModalAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const impressionTracked = useRef(false)
  const impressionId = useRef<string | null>(null)
  // Viewer size, so a fixed-size creative can be scaled to fill it properly
  // instead of sitting at native size in the corner of a huge black frame.
  const [box, setBox] = useState<{ w: number; h: number } | null>(null)

  useEffect(() => {
    if (!hasConsent || effectivePremiumUser) return
    // Respect cooldown — don't show if user saw one recently
    if (!isCooldownExpired(cooldownMinutes)) {
      setLoaded(true)
      return
    }

    fetch(`/api/ads/network-grid-ad?type=grid&tier=${adTier}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.zone_id) {
          setAdData(d)
          // Only the "full" instance — the one actually shown to the user as
          // a real ad slide — should consume the shared cooldown. The
          // thumbnail strip renders a "thumb" instance for every ad slot up
          // front (see AlbumStripAdWrapper), all mounting the moment the
          // album opens. If those also marked cooldown, they'd burn it
          // before the user ever paged to the slot, so by the time the real
          // "full" instance mounted for that slot, isCooldownExpired() below
          // was already false — it never fetched, adData stayed null, and
          // the dismiss-on-no-ad effect below silently skipped the slide.
          // That's the "ad gets skipped over as I click through" report.
          if (variant === "full") markCooldown()
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [hasConsent, effectivePremiumUser, cooldownMinutes, adTier, variant])

  // No ad to show — cooldown blocked the fetch, or the fetch genuinely came
  // back with no active zone. Either way, this slot should never be a dead
  // blank frame the user has to manually page past (no badge, no dismiss
  // button render at all once !adData — see the early return below): skip
  // straight to the real page it was inserted in front of, same as
  // dismissing it manually.
  useEffect(() => {
    if (loaded && !adData) onDismiss?.()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, adData])

  // Track the viewer's real size. Runs after adData lands, since that's when
  // the container actually renders.
  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [adData])

  // Impression tracking
  useEffect(() => {
    if (!adData || impressionTracked.current || !containerRef.current) return
    const el = containerRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !impressionTracked.current) {
          impressionTracked.current = true
          fetch("/api/ads/zone-interact", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ zone_id: adData.zone_id, event: "impression" }),
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (d?.impression_id) impressionId.current = d.impression_id })
            .catch(() => {})
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [adData, slotIndex])

  if (!hasConsent || effectivePremiumUser || dismissed || !loaded || !adData) return null

  const handleDismiss = () => {
    setDismissed(true)
    onDismiss?.()
  }

  const trackClick = () => {
    fetch("/api/ads/zone-interact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        zone_id: adData.zone_id,
        event: "click",
        impression_id: impressionId.current,
      }),
    }).catch(() => {})
  }

  // ── Full variant ────────────────────────────────────────────────────────────
  if (variant === "full") {
    return (
      <div
        ref={containerRef}
        className={cn(
          "relative w-full h-full flex items-center justify-center bg-black/90 overflow-hidden",
          className
        )}
      >
        <div className="absolute top-3 left-3 z-10">
          <Badge variant="secondary" className="text-[10px] bg-black/60 text-white/70 backdrop-blur-sm border-white/10">
            {t("ad")}
          </Badge>
        </div>
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80 text-white/70 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
          aria-label={t("closeAd")}
        >
          <X className="h-3.5 w-3.5" />
        </button>

        {(adData.zone_type === "js_embed" || adData.zone_type === "html_embed") && (() => {
          // A real cross-origin iframe for a recognized ExoClick zone tag —
          // see lib/ad-embed.ts. Anything else falls back to a sandboxed
          // srcDoc iframe (scripts run, but contained to their own frame)
          // rather than injecting directly into our own document, which
          // used to give a third-party ad script full, unsandboxed run of
          // this page — the exact "ad script hijacks clicks elsewhere on
          // the page" incident already fixed on every other ad slot.
          const zoneId = extractExoclickZoneId(adData.embed_code)
          const src = zoneId ? exoclickIframeSrc(zoneId) : undefined
          const srcDoc = zoneId
            ? undefined
            : `<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${adData.embed_code ?? ""}</body></html>`
          const sandbox = zoneId ? "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" : "allow-scripts"

          const nativeW = adData.creative_width
          const nativeH = adData.creative_height

          // Known native size — render the frame at exactly that size and
          // scale it from its centre. The parent is already
          // `flex items-center justify-center`, so laying the frame out at its
          // true size (rather than w-full/h-full) is what actually centres the
          // creative; scaling then makes it fill the viewer properly instead
          // of floating small in the middle of it.
          if (nativeW && nativeH && nativeW > 0 && nativeH > 0) {
            const scale = box
              ? Math.min(
                  (box.w * GALLERY_FILL_RATIO) / nativeW,
                  (box.h * GALLERY_FILL_RATIO) / nativeH,
                  MAX_GALLERY_UPSCALE,
                )
              : 1
            return (
              <iframe
                src={src}
                srcDoc={srcDoc}
                sandbox={sandbox}
                title={t("advertisement")}
                scrolling="no"
                width={nativeW}
                height={nativeH}
                style={{
                  width: nativeW,
                  height: nativeH,
                  border: 0,
                  display: "block",
                  transform: `scale(${scale})`,
                  transformOrigin: "center",
                }}
              />
            )
          }

          // Size unknown (e.g. a srcDoc embed with no configured zone size) —
          // keep the previous fill behavior.
          return (
            <iframe
              src={src}
              srcDoc={srcDoc}
              sandbox={sandbox}
              title={t("advertisement")}
              scrolling="no"
              className="w-full h-full border-0"
            />
          )
        })()}

        {adData.zone_type === "banner_image" && adData.banner_image_url && (
          <a
            href={adData.banner_click_url ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full h-full"
            onClick={trackClick}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={adData.banner_image_url} alt={t("advertisement")} className="w-full h-full object-contain" />
            <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-white/50">
              <ExternalLink className="h-3 w-3" /><span>{t("visit")}</span>
            </div>
          </a>
        )}
      </div>
    )
  }

  // ── Thumb variant ───────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex-shrink-0 rounded overflow-hidden cursor-pointer border border-white/10 bg-black/60 flex items-center justify-center",
        className
      )}
      style={{ width: 48, height: 48 }}
    >
      <Badge variant="secondary" className="absolute top-0.5 left-0.5 text-[8px] px-1 py-0 h-3.5 bg-black/70 text-white/60 border-0">
        {t("ad")}
      </Badge>
      {adData.zone_type === "banner_image" && adData.banner_image_url ? (
        <a href={adData.banner_click_url ?? "#"} target="_blank" rel="noopener noreferrer" className="block w-full h-full" onClick={trackClick}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={adData.banner_image_url} alt={t("ad")} className="w-full h-full object-cover" />
        </a>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ExternalLink className="h-3.5 w-3.5 text-white/30" />
        </div>
      )}
    </div>
  )
}
